import React, { useEffect } from "react";
import {
  Box,
  Autocomplete,
  Modal,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useState } from "react";
import { createFilterOptions } from "@mui/material/Autocomplete";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import ReportNavigation from "../../Components/ReportNavigation/ReportNavigation";
import Task from "@mui/icons-material/Task";

const PlacingList = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const [open, setOpen] = useState(false);

  // existing inward list
  const [param, setParam] = useState([]);

  // toggle: inward vs partywise
  const [mode, setMode] = useState("inward");

  const OPTIONS_LIMIT = 15;
  const filterOptions = createFilterOptions({
    limit: OPTIONS_LIMIT,
    trim: true,
  });

  const [searchInput, setSearchInput] = useState(null); // inward selected row
  const [invnoText, setInvnoText] = useState(""); // typed invno for partywise
  const [pdfStream, setPdfStream] = useState(null);

  // ✅ loader state for Show Report
  const [loadingReport, setLoadingReport] = useState(false);

  async function getParam() {
    try {
      const response = await fetch("/reports/purbilldetails", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setParam(jsonData);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getParam();
  }, []);

  const handleClose = () => {
    if (loadingReport) return; // prevent close while loading
    setOpen(false);
  };

  const handleModeChange = (e, newMode) => {
    if (!newMode) return;
    if (loadingReport) return;

    setMode(newMode);
    setPdfStream(null); // reset preview on switch
  };

  const showDisabled =
    loadingReport ||
    (mode === "inward"
      ? !searchInput?.inwardid
      : String(invnoText || "").trim().length === 0);

  const handleReport = async (e) => {
    e.preventDefault();
    if (showDisabled) return;

    setLoadingReport(true);
    try {
      // INWARD mode (existing report)
      if (mode === "inward") {
        const inwardid = searchInput?.inwardid;
        if (!inwardid) return;

        const pdfBlob = await fetch(`/reports/getplacinglist/${inwardid}`, {
          method: "GET",
          headers: { sptoken: userToken },
        }).then((r) => r.blob());

        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfStream(pdfUrl);
        return;
      }

      // PARTYWISE mode (typed invno)
      const invno = String(invnoText || "").trim();
      if (!invno) return;

      const pdfBlob = await fetch(`/reports/getplacinglist/partywise`, {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ invno }),
      }).then((r) => r.blob());

      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfStream(pdfUrl);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div>
      <Button
        onClick={() => setOpen(true)}
        variant="contained"
        color="secondary"
        size="large"
        sx={{ boxShadow: "2px 2px 6px  #404142" }}
      >
        Placing List
      </Button>

      <Modal
        open={open}
        aria-labelledby="modl-box"
        aria-describedby="search partitem"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 6,
            mt: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "20px",
              rowGap: "10px",
              alignItems: "center",
            }}
          >
            {/* Toggle */}
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
              disabled={loadingReport}
            >
              <ToggleButton value="inward">Inward</ToggleButton>
              <ToggleButton value="partywise">Partywise</ToggleButton>
            </ToggleButtonGroup>

            {/* Input changes based on mode */}
            {mode === "inward" ? (
              <Autocomplete
                id="inward"
                value={searchInput}
                filterOptions={filterOptions}
                onChange={(event, newValue) => {
                  setSearchInput(newValue);
                }}
                options={param}
                getOptionLabel={(option) => option.inwardno}
                sx={{ width: "30%" }}
                renderInput={(billopts) => (
                  <TextField
                    required
                    {...billopts}
                    placeholder="Inward No"
                    disabled={loadingReport}
                  />
                )}
                size="small"
                disabled={loadingReport}
              />
            ) : (
              <TextField
                size="small"
                value={invnoText}
                onChange={(e) => setInvnoText(e.target.value)}
                placeholder="Invoice No (type here)"
                sx={{ width: "30%" }}
                required
                disabled={loadingReport}
              />
            )}

            {/* ✅ Show button with loader */}
            <Button
              type="submit"
              onClick={handleReport}
              variant="contained"
              component="label"
              size="large"
              sx={{ boxShadow: "2px 2px 6px  #404142", minWidth: 160 }}
              endIcon={
                loadingReport ? null : (
                  <Task />
                )
              }
              disabled={showDisabled}
            >
              {loadingReport ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Show Report"
              )}
            </Button>

            {/* Close */}
            <Button
              variant="contained"
              size="medium"
              onClick={handleClose}
              color="error"
              disabled={loadingReport}
            >
              Close
            </Button>
          </Box>

          {pdfStream && <ReportNavigation />}

          <Box sx={{ backgroundColor: "white" }}>
            {pdfStream && (
              <iframe
                src={pdfStream}
                width="100%"
                height="350px"
                title="report"
              ></iframe>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default PlacingList;
